import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from './AuthContext'
import { saveUserProfile, getUserProfiles, getUserProfile, deleteUserProfile } from '../firebase/firestoreService'

// Create the context
const ProfileContext = createContext()

// Custom hook to use the profile context
export const useProfiles = () => {
  return useContext(ProfileContext)
}

// Validate profile data
const validateProfile = (profileData) => {
  // Check for work experience with title
  const hasWorkExperience =
    profileData?.experience?.entries?.length > 0 && profileData.experience.entries.some((entry) => entry.role && entry.role.trim() !== '')

  // Check for contact name
  const hasContactName = profileData?.contacts?.name && profileData.contacts.name.trim() !== ''

  // Check for contact email
  const hasContactEmail =
    profileData?.contacts?.email?.length > 0 && profileData.contacts.email.some((email) => email.value && email.value.trim() !== '')

  // Build error message based on missing fields
  const errors = []
  if (!hasWorkExperience) errors.push('At least one work experience with a Job Role is required')
  if (!hasContactName) errors.push('Contact name is required')
  if (!hasContactEmail) errors.push('Contact email is required')

  return {
    isValid: hasWorkExperience && hasContactName && hasContactEmail,
    error: errors.length > 0 ? `Missing required fields: ${errors.join(', ')}` : '',
  }
}

// Provider component
export const ProfileProvider = ({ children }) => {
  const { currentUser } = useAuth()
  const [profiles, setProfiles] = useState([])
  const [activeProfile, setActiveProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  // Load user profiles when user changes
  useEffect(() => {
    const loadProfiles = async () => {
      if (currentUser) {
        setLoading(true)
        try {
          const result = await getUserProfiles(currentUser.uid)
          if (result.success) {
            setProfiles(result.profiles)
          } else {
            console.error('Error loading profiles:', result.error)
            setProfiles([])
          }
        } catch (error) {
          console.error('Error in loadProfiles:', error)
          setProfiles([])
        } finally {
          setLoading(false)
        }
      } else {
        setProfiles([])
        setActiveProfile(null)
        setLoading(false)
      }
    }

    loadProfiles()
  }, [currentUser])

  // Save a profile
  const saveProfile = async (profileData, profileName) => {
    if (!currentUser) return { success: false, error: 'User not authenticated' }

    // Validate profile data before saving
    const validation = validateProfile(profileData)
    if (!validation.isValid) {
      return { success: false, error: validation.error }
    }

    try {
      const result = await saveUserProfile(currentUser.uid, profileName, profileData)

      if (result.success) {
        // Refresh the profiles list
        const updatedProfiles = await getUserProfiles(currentUser.uid)
        if (updatedProfiles.success) {
          setProfiles(updatedProfiles.profiles)
        }
      }

      return result
    } catch (error) {
      console.error('Error in saveProfile:', error)
      return { success: false, error: error.message }
    }
  }

  // Load a specific profile
  const loadProfile = async (profileId) => {
    if (!currentUser) return { success: false, error: 'User not authenticated' }

    try {
      setLoading(true)
      const result = await getUserProfile(currentUser.uid, profileId)

      if (result.success) {
        setActiveProfile({
          id: profileId,
          data: result.data,
        })
      }

      return result
    } catch (error) {
      console.error('Error in loadProfile:', error)
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  // Delete a profile
  const deleteProfile = async (profileId) => {
    if (!currentUser) return { success: false, error: 'User not authenticated' }

    try {
      const result = await deleteUserProfile(currentUser.uid, profileId)

      if (result.success) {
        // Update the profiles list
        setProfiles(profiles.filter((profile) => profile.id !== profileId))

        // Clear the active profile if it was deleted
        if (activeProfile && activeProfile.id === profileId) {
          setActiveProfile(null)
        }
      }

      return result
    } catch (error) {
      console.error('Error in deleteProfile:', error)
      return { success: false, error: error.message }
    }
  }

  // Create a new active profile
  const createNewProfile = () => {
    // Get the latest contact information from the most recent profile
    let latestContacts = {
      email: '',
      phone: '',
      location: '',
      linkedIn: '',
      website: '',
    }

    // Find the most recent profile
    if (profiles && profiles.length > 0) {
      // Attempt to load the contact details from the most recent profile
      const profilesWithData = profiles.filter((profile) => profile.data && profile.data.contacts)

      if (profilesWithData.length > 0) {
        const latestProfile = profilesWithData[0] // Most recent profile should be first in the array
        latestContacts = latestProfile.data.contacts
      }
    }

    // Create a new profile with default values but include the latest contact details
    setActiveProfile({
      id: null,
      data: {
        experience: {
          entries: [
            {
              id: Date.now(),
              content: '',
              role: '',
              company: '',
              fromDate: '',
              untilDate: '',
            },
          ],
        },
        education: {
          entries: [
            {
              id: Date.now(),
              content: '',
              course: '',
              university: '',
              fromDate: '',
              untilDate: '',
            },
          ],
        },
        skills: {
          entries: [
            {
              id: Date.now(),
              content: '',
            },
          ],
        },
        contacts: latestContacts,
      },
    })
  }

  // Value to be provided by the context
  const value = {
    profiles,
    activeProfile,
    loading,
    saveProfile,
    loadProfile,
    deleteProfile,
    createNewProfile,
  }

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
}

export default ProfileContext
